import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { reviews, faqs } from '@/mocks/comics';
import { useState } from 'react';

export default function AboutPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const hpField = formData.get('website_alt');
    if (hpField && String(hpField).trim() !== '') {
      setFormStatus('success');
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    try {
      const res = await fetch('http://metcw.com/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Array.from(formData.entries())
            .filter(([key]) => key !== 'website_alt')
            .map(([k, v]) => [k, String(v)])
        ).toString(),
      });

      const text = await res.text();
      let parsed: Record<string, unknown> = {};
      try {
        parsed = JSON.parse(text);
      } catch {
        // not json
      }

      if (res.ok && (parsed as { code?: string }).code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg: string =
          (parsed as { meta?: { message?: string } }).meta?.message ||
          (parsed as { message?: string }).message ||
          (parsed as { meta?: { detail?: string } }).meta?.detail ||
          text ||
          '提交失败，请稍后再试';
        if (serverMsg.toLowerCase().includes('spam')) {
          setFormError('提交失败，请稍后再试');
        } else {
          setFormError(serverMsg);
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  const milestones = [
    { year: '2020', title: '平台创立', desc: '虚空韩漫网正式上线，致力于为中文读者提供正版韩漫阅读服务。' },
    { year: '2021', title: '版权合作', desc: '与韩国多家知名漫画平台达成版权合作协议，正版资源突破1000部。' },
    { year: '2022', title: '阅读体验升级', desc: '全面优化下拉式阅读体验，推出个性化推荐算法，活跃用户突破500万。' },
    { year: '2023', title: '内容扩展', desc: '引入更多类型韩漫，新增悬疑、奇幻等品类，正版资源突破3000部。' },
    { year: '2024', title: '行业领先', desc: '成为中文互联网最大的正版韩漫阅读平台之一，正版资源突破5000部。' },
    { year: '2025', title: '持续创新', desc: '推出AI智能推荐、社区互动等新功能，持续提升用户体验。' },
  ];

  return (
    <>
      <Navbar />
      <main>
        <section className="relative w-full min-h-[420px] md:min-h-[520px] flex items-center overflow-hidden bg-background-50 pt-16">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Modern%20creative%20office%20space%20with%20comic%20art%20on%20walls%2C%20warm%20ambient%20lighting%2C%20dark%20sophisticated%20atmosphere%2C%20professional%20workspace%2C%20korean%20webtoon%20style%20decorations%2C%20cozy%20yet%20modern%20interior%2C%20clean%20composition&width=1600&height=900&seq=about-hero-01&orientation=landscape"
              alt="虚空韩漫网 - 关于我们"
              className="w-full h-full object-cover object-top opacity-30"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-background-50/90 via-background-50/60 to-background-50"></div>
          </div>

          <div className="relative w-full max-w-[1440px] mx-auto px-4 md:px-6 lg:px-10">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6" aria-label="面包屑导航">
              <a href="/" className="hover:text-foreground-300 transition-colors">首页</a>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-200 font-medium">关于我们</span>
            </nav>
            <span className="inline-block px-3 py-1 text-xs font-medium text-foreground-400 bg-background-200/60 rounded-full mb-6">
              品牌故事
            </span>
            <h1 className="font-heading text-4xl md:text-6xl lg:text-7xl font-black text-foreground-50 leading-tight">
              虚空韩漫
              <br />
              正版漫画
              <br />
              阅读平台
            </h1>
            <p className="mt-6 text-base md:text-lg text-foreground-400 max-w-xl leading-relaxed">
              虚空韩漫网成立于2020年，是专注于正版韩漫的在线阅读平台。我们与韩国多家知名漫画出版社和平台建立了深度合作关系，致力于为中国读者带来最优质、最全面的正版韩漫阅读体验。在尊重版权的同时，让每一位漫画爱好者都能畅享阅读的乐趣。
            </p>
          </div>
        </section>

        <section className="w-full py-16 md:py-24 bg-background-100">
          <div className="w-full max-w-[1440px] mx-auto px-4 md:px-6 lg:px-10">
            <div className="text-center mb-16">
              <span className="inline-block px-3 py-1 text-xs font-medium text-foreground-400 bg-background-200/60 rounded-full mb-4">
                平台声誉
              </span>
              <h2 className="font-heading text-3xl md:text-5xl font-bold text-foreground-50">
                我们的成长
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {milestones.map((m) => (
                <div key={m.year} className="p-6 rounded-2xl bg-background-200/50 hover:bg-background-200/70 transition-colors duration-200">
                  <span className="text-3xl font-heading font-bold text-accent-500">{m.year}</span>
                  <h3 className="text-lg font-semibold text-foreground-200 mt-3 mb-2">{m.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{m.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full py-16 md:py-24 bg-background-50">
          <div className="w-full max-w-[1440px] mx-auto px-4 md:px-6 lg:px-10">
            <div className="mb-12">
              <span className="inline-block px-3 py-1 text-xs font-medium text-foreground-400 bg-background-200/60 rounded-full mb-4">
                读者口碑
              </span>
              <h2 className="font-heading text-3xl md:text-5xl font-bold text-foreground-50">
                他们这样说
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[reviews.slice(0, 2), reviews.slice(2, 4), reviews.slice(4, 6)].map((col, colIdx) => (
                <div key={colIdx} className="flex flex-col gap-6">
                  {col.map((review) => (
                    <div key={review.id} className="p-6 rounded-2xl bg-background-100/60">
                      <div className="flex items-center gap-3 mb-4">
                        <img src={review.avatar} alt={review.username} className="w-10 h-10 rounded-full object-cover" />
                        <div>
                          <div className="text-sm font-semibold text-foreground-200">{review.username}</div>
                          <div className="text-xs text-foreground-600">{review.handle}</div>
                        </div>
                      </div>
                      <p className="text-sm text-foreground-400 leading-relaxed">&ldquo;{review.content}&rdquo;</p>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full py-16 md:py-24 bg-background-100">
          <div className="w-full max-w-[1440px] mx-auto px-4 md:px-6 lg:px-10">
            <div className="flex flex-col lg:flex-row gap-12 lg:gap-20">
              <div className="lg:w-1/2">
                <span className="inline-block px-3 py-1 text-xs font-medium text-foreground-400 bg-background-200/60 rounded-full mb-4">
                  联系我们
                </span>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-50 mb-6">
                  期待与您合作
                </h2>
                <p className="text-base text-foreground-400 leading-relaxed mb-8">
                  商务合作、版权引进、广告投放、媒体采访，欢迎通过以下方式联系我们。
                </p>
                <div className="flex flex-col gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-background-200/60 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-foreground-400"></i>
                    </div>
                    <span className="text-sm text-foreground-300">hello@metcw.com</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-background-200/60 flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-foreground-400"></i>
                    </div>
                    <span className="text-sm text-foreground-300">400-888-6666</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-background-200/60 flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-foreground-400"></i>
                    </div>
                    <span className="text-sm text-foreground-300">北京市朝阳区望京SOHO T1 18层</span>
                  </div>
                </div>
              </div>

              <div className="lg:w-1/2">
                <form
                  id="contact-form"
                 
                  className="p-8 rounded-2xl bg-background-200/40"
                  onSubmit={handleSubmit}
                >
                  <h3 className="text-lg font-semibold text-foreground-200 mb-6">发送消息</h3>

                  <div className="flex flex-col gap-4">
                    <input
                      type="text"
                      name="name"
                      placeholder="您的姓名"
                      required
                      className="w-full px-4 py-3 text-sm bg-background-50 border border-background-300/40 rounded-xl text-foreground-200 placeholder-foreground-600 focus:outline-none focus:border-primary-500/50 transition-colors"
                    />
                    <input
                      type="email"
                      name="email"
                      placeholder="您的邮箱"
                      required
                      className="w-full px-4 py-3 text-sm bg-background-50 border border-background-300/40 rounded-xl text-foreground-200 placeholder-foreground-600 focus:outline-none focus:border-primary-500/50 transition-colors"
                    />
                    <input
                      type="text"
                      name="subject"
                      placeholder="主题"
                      required
                      className="w-full px-4 py-3 text-sm bg-background-50 border border-background-300/40 rounded-xl text-foreground-200 placeholder-foreground-600 focus:outline-none focus:border-primary-500/50 transition-colors"
                    />
                    <textarea
                      name="message"
                      placeholder="您的留言（500字以内）"
                      rows={4}
                      maxLength={500}
                      required
                      className="w-full px-4 py-3 text-sm bg-background-50 border border-background-300/40 rounded-xl text-foreground-200 placeholder-foreground-600 focus:outline-none focus:border-primary-500/50 transition-colors resize-none"
                    ></textarea>

                    <div className="hp-field-wrap">
                      <input
                        type="text"
                        name="website_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={formStatus === 'submitting'}
                      className="w-full py-3 bg-primary-500 hover:bg-primary-600 disabled:opacity-60 text-foreground-50 font-medium text-sm rounded-full transition-colors duration-200 whitespace-nowrap cursor-pointer"
                    >
                      {formStatus === 'submitting' ? '发送中...' : '发送消息'}
                    </button>

                    {formStatus === 'success' && (
                      <p className="text-sm text-green-400 text-center">消息已发送成功，我们会尽快回复您！</p>
                    )}
                    {formStatus === 'error' && formError && (
                      <p className="text-sm text-red-400 text-center">{formError}</p>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full py-16 md:py-24 bg-background-50" id="faq">
          <div className="w-full max-w-[1440px] mx-auto px-4 md:px-6 lg:px-10">
            <div className="text-center mb-12">
              <span className="inline-block px-3 py-1 text-xs font-medium text-foreground-400 bg-background-200/60 rounded-full mb-4">
                常见问题
              </span>
              <h2 className="font-heading text-3xl md:text-5xl font-bold text-foreground-50">
                您可能想了解
              </h2>
            </div>
            <div className="max-w-3xl mx-auto">
              <div className="flex flex-col gap-3">
                {faqs.map((faq, idx) => (
                  <div key={idx} className="rounded-2xl bg-background-100/60 border border-background-200/40 overflow-hidden">
                    <button
                      className="w-full flex items-center justify-between p-5 md:p-6 text-left cursor-pointer"
                      onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                    >
                      <span className="text-sm md:text-base font-medium text-foreground-200 pr-4">
                        {faq.question}
                      </span>
                      <i className={`ri-arrow-down-s-line text-lg text-foreground-500 flex-shrink-0 transition-transform duration-300 ${openFaq === idx ? 'rotate-180' : ''}`}></i>
                    </button>
                    <div className={`overflow-hidden transition-all duration-300 ${openFaq === idx ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                      <p className="px-5 md:px-6 pb-5 md:pb-6 text-sm text-foreground-500 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首页',
                item: 'http://metcw.com/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: '关于我们',
              },
            ],
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于虚空韩漫网',
            description: '虚空韩漫网成立于2020年，是专注于正版韩漫的在线阅读平台。',
            inLanguage: 'zh-CN',
          }),
        }}
      />
    </>
  );
}